import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useColorConfig } from '../../utils/colorConfig';

const BarChart = ({ data = [], maxValue, title }) => {
  const palette = useColorConfig();
  const fallbackColors = [
    palette.primary,
    palette.secondary,
    palette.success,
    palette.warning,
    palette.error,
    palette.info,
  ].filter(Boolean);

  const safeData = useMemo(
    () =>
      data.map((d, idx) => ({
        label: d.label,
        value: Number(d.value) || 0,
        color: d.color || fallbackColors[idx % fallbackColors.length] || palette.primary,
      })),
    [data, fallbackColors, palette.primary]
  );

  const max = useMemo(() => {
    if (typeof maxValue === 'number' && maxValue > 0) return maxValue;
    return safeData.reduce((m, d) => Math.max(m, d.value), 0) || 1;
  }, [safeData, maxValue]);

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {title && <Typography variant="subtitle1">{title}</Typography>}
      <Stack spacing={1.5}>
        {safeData.map((item) => {
          const width = `${Math.min(100, (item.value / max) * 100)}%`;
          return (
            <Stack key={item.label} spacing={0.5} title={`${item.label}: ${item.value}`}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.primary" noWrap title={item.label}>
                  {item.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.value}
                </Typography>
              </Stack>
              <Box
                role="presentation"
                aria-label={`${item.label}: ${item.value}`}
                sx={{
                  position: 'relative',
                  height: 10,
                  borderRadius: 6,
                  backgroundColor: palette.borderSubtle || palette.divider,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width,
                    backgroundColor: item.color,
                    borderRadius: 6,
                    transition: 'width 200ms ease',
                  }}
                />
              </Box>
            </Stack>
          );
        })}
        {safeData.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No data
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default BarChart;
